import React, { useEffect } from 'react';
import SEOHead from '@/components/seo/SEOHead';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Footer } from '@/components/layout/Footer';
import { CheckCircle2 } from 'lucide-react';
import Icon from '@/components/ui/icon-map';
import { CAREGIVER_FEATURES } from '@/constants/app';
import CaregiverFeature from '@/components/ui/caregiver-feature';
import CaregiverDashboard from '@/components/ui/caregiver-dashboard';

const caregiversSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "name": "Meduler for Caregivers — Caregiver Medication Management",
      "description": "Meduler helps caregivers manage medications for elderly parents and loved ones with shared profiles, missed-dose alerts, and a simple schedule view — free to start.",
      "url": "https://www.meduler.com/caregivers",
      "audience": {
        "@type": "Audience",
        "audienceType": "Caregivers, Family Members, Home Health Nurses"
      },
      "mainEntity": {
        "@type": "SoftwareApplication",
        "name": "Meduler",
        "applicationCategory": "HealthApplication",
        "offers": [
          {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Free caregiver medication management with dual user profiles"
          },
          {
            "@type": "Offer",
            "price": "4.99",
            "priceCurrency": "USD",
            "description": "Meduler Plus with caregiver alerts and advanced shared access"
          }
        ]
      }
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Can I manage medications for my elderly parent using Meduler?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Meduler's free Starter plan includes dual user profiles, so you can manage your own medications and a loved one's schedule from the same app — no extra account needed."
          }
        },
        {
          "@type": "Question",
          "name": "Will I be notified if my parent misses a dose?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Caregiver missed-dose alerts are a Meduler Plus feature. With Plus, you receive a notification whenever a scheduled dose goes unconfirmed, giving you peace of mind when you're not present."
          }
        },
        {
          "@type": "Question",
          "name": "Can multiple family members share access to one medication schedule?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Meduler Plus supports shared access across multiple caregivers, so siblings, spouses, or care team members can all stay informed about the same loved one's medication status."
          }
        },
        {
          "@type": "Question",
          "name": "Is Meduler suitable for home health nurses managing multiple patients?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Meduler is designed for family caregivers and individuals rather than clinical multi-patient workflows. Home health nurses often use it to set up and demonstrate medication schedules for their patients."
          }
        },
        {
          "@type": "Question",
          "name": "Does Meduler work offline for caregivers who may have limited connectivity?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Meduler is offline-first. The complete medication schedule, reminders, and history work without Wi-Fi or mobile data — ideal for caregivers in areas with limited connectivity."
          }
        }
      ]
    }
  ]
};

const CaregiversPage: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && 'gtag' in window) {
      (window as any).gtag('event', 'page_view', {
        page_title: 'Meduler for Caregivers',
        page_path: window.location.pathname,
      });
    }
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update().catch(() => {});
      });
    }
  }, []);

  return (
    <>
      <SEOHead
        title="Caregiver Medication Management — Meduler"
        description="Meduler helps caregivers manage medications for elderly parents and loved ones with shared profiles, missed-dose alerts, and a simple schedule — free to start."
        keywords="caregiver medication management, medication app for caregivers, manage medications for elderly parents, missed dose alerts, shared medication schedule"
        canonicalUrl="https://www.meduler.com/caregivers"
        image="https://www.meduler.com/social/og-image.png"
        structuredData={caregiversSchema}
      />

      <main className="min-h-screen bg-gradient-to-b from-white via-purple-50 to-white">
        {/* Hero Section — GEO template: question-format H1 + direct-answer paragraph */}
        <section className="pt-4 md:pt-6 pb-12 px-4 md:px-8">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-block px-3 py-1 mb-4 rounded-full bg-purple-100 border border-purple-200">
              <p className="text-sm font-medium text-purple-700">For Family Members & Medical Professionals</p>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              How Can Caregivers Manage Medications with <span className="text-purple-600">Meduler?</span>
            </h1>
            {/* Direct-answer paragraph — within first 100 words */}
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
              Meduler gives caregivers a free, offline-first tool to manage medications for elderly parents and loved ones. The Starter plan includes dual user profiles so you can track multiple schedules in one place. Meduler Plus adds missed-dose alerts and shared access for entire care teams.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <a 
                href="/" 
                className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white font-medium rounded-lg shadow-sm hover:bg-purple-700 transition-colors"
              >
                Start for Free
              </a>
              <a 
                href="/how-it-works" 
                className="inline-flex items-center justify-center px-6 py-3 bg-white border border-purple-200 text-purple-600 font-medium rounded-lg shadow-sm hover:bg-purple-50 transition-colors"
              >
                See How It Works
              </a>
            </div>
          </motion.div>
        </section>

        {/* The Meduler Caregiver System — named framework */}
        <section className="py-12 px-4 md:px-8 bg-white" aria-labelledby="caregiver-system-heading">
          <div className="max-w-4xl mx-auto">
            <h2 id="caregiver-system-heading" className="text-2xl font-bold text-center text-gray-900 mb-3">
              The Meduler Caregiver Coordination System
            </h2>
            <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
              Four practices caregivers use to keep a loved one's medications on track — without the overwhelm.
            </p>
            <ol className="space-y-5">
              {[
                { step: 1, title: "Create a shared profile", desc: "Add your loved one as a second profile in Meduler. Enter their medications, dosages, and schedules — all separate from your own." },
                { step: 2, title: "Set caregiver reminders", desc: "Configure reminders that work for your monitoring style — check-in notifications, missed-dose alerts (Plus), or a simple daily schedule view." },
                { step: 3, title: "Monitor adherence remotely", desc: "Adherence logs let you see at a glance whether doses have been confirmed. Share the weekly report with a physician at each appointment." },
                { step: 4, title: "Coordinate with your care team", desc: "Meduler Plus allows multiple caregivers to share access to the same profile, so siblings or professional aides all stay in sync." },
              ].map(({ step, title, desc }) => (
                <li key={step} className="flex gap-5 p-5 rounded-xl bg-purple-50 border border-purple-100">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white font-bold flex items-center justify-center text-lg">{step}</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Caregivers Main Content */}
        <section 
          className="py-16 px-4 md:px-8"
          ref={ref}
        >
          <motion.div 
            className="max-w-7xl mx-auto"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <motion.div 
                className="opacity-100"
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="inline-block px-3 py-1 mb-5 rounded-full bg-purple-100 border border-purple-200">
                  <p className="text-sm font-medium text-purple-700">For Caregivers & Family Members</p>
                </div>
                
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Manage Medications for Elderly Parents and Loved Ones
                </h2>
                
                <p className="text-lg text-gray-600 mb-8">
                  Meduler provides specialized tools for caregivers managing medications for family members. Our caregiver-friendly medication management features give you peace of mind and help ensure medication adherence.
                </p>
                
                <div className="space-y-5">
                  {CAREGIVER_FEATURES.map((feature, index) => (
                    <CaregiverFeature
                      key={index}
                      icon={feature.icon}
                      title={feature.title}
                      description={feature.description}
                    />
                  ))}
                </div>
              </motion.div>
              
              {/* Visual */}
              <motion.div 
                className="relative"
                initial={{ opacity: 0, x: 20 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <CaregiverDashboard />
              </motion.div>
            </div>
          </motion.div>
        </section>

        {/* Segment-specific advice */}
        <section className="py-12 px-4 md:px-8 bg-white" aria-labelledby="segment-caregivers-heading">
          <div className="max-w-4xl mx-auto">
            <h2 id="segment-caregivers-heading" className="text-2xl font-bold text-center text-gray-900 mb-8">
              Meduler for Different Caregiver Situations
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl bg-purple-50 border border-purple-100">
                <h3 className="font-bold text-gray-900 mb-3">Caring for an aging parent</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Many older adults take five or more medications daily. Meduler's dual profile system lets you maintain your parent's full schedule alongside your own, and the calendar view helps you quickly confirm what was taken each day.
                </p>
              </div>
              <div className="p-6 rounded-xl bg-purple-50 border border-purple-100">
                <h3 className="font-bold text-gray-900 mb-3">Coordinating with siblings or a care team</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  When multiple people share caregiving responsibilities, miscommunication about medications is a serious risk. Meduler Plus shared access ensures everyone sees the same real-time schedule and adherence log.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Limitations section */}
        <section className="py-10 px-4 md:px-8 bg-gray-50 border-y border-gray-100" aria-labelledby="limitations-caregivers-heading">
          <div className="max-w-3xl mx-auto">
            <h2 id="limitations-caregivers-heading" className="text-xl font-bold text-gray-800 mb-4">When Meduler isn't the right fit for caregivers</h2>
            <ul className="space-y-2 text-gray-600 text-sm list-disc list-inside">
              <li>Meduler is not a clinical tool and does not integrate with hospital or pharmacy systems.</li>
              <li>It does not check for drug interactions — always work with the prescribing physician or pharmacist.</li>
              <li>Professional care agencies managing large numbers of patients should consider purpose-built clinical software.</li>
            </ul>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-16 px-4 md:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">What Caregivers Say About Meduler</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.div 
                className="bg-purple-50 p-6 rounded-xl shadow-sm border border-purple-100"
                whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                transition={{ duration: 0.2 }}
              >
                <div className="mb-4 text-purple-500">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  "Meduler has been a game-changer for managing my mother's medications. I can check if she's taken her meds even when I'm not there. The peace of mind is invaluable."
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center mr-3">
                    <span className="text-purple-700 font-medium">SL</span>
                  </div>
                  <div>
                    <p className="font-medium">Sarah L.</p>
                    <p className="text-sm text-gray-500">Caring for elderly mother</p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                className="bg-purple-50 p-6 rounded-xl shadow-sm border border-purple-100"
                whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                transition={{ duration: 0.2 }}
              >
                <div className="mb-4 text-purple-500">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  "As a home health nurse, I recommend Meduler to all my patients. The ability to share medication schedules with multiple family members keeps everyone on the same page."
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center mr-3">
                    <span className="text-purple-700 font-medium">JM</span>
                  </div>
                  <div>
                    <p className="font-medium">James M., RN</p>
                    <p className="text-sm text-gray-500">Home Health Nurse</p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                className="bg-purple-50 p-6 rounded-xl shadow-sm border border-purple-100 md:col-span-2 lg:col-span-1"
                whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                transition={{ duration: 0.2 }}
              >
                <div className="mb-4 text-purple-500">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  "Coordinating my dad's complex medication schedule with my siblings used to be a nightmare. With Meduler Plus, we all get alerts if a dose is missed, and the reporting helps us communicate better with his doctors."
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center mr-3">
                    <span className="text-purple-700 font-medium">AT</span>
                  </div>
                  <div>
                    <p className="font-medium">Alex T.</p>
                    <p className="text-sm text-gray-500">Family Caregiver</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 px-4 md:px-8" aria-labelledby="faq-caregivers-heading">
          <div className="max-w-3xl mx-auto">
            <h2 id="faq-caregivers-heading" className="text-2xl font-bold text-center text-gray-900 mb-8">
              Caregiver FAQ
            </h2>
            <div className="space-y-4">
              {[
                { q: "Can I manage medications for my elderly parent using Meduler?", a: "Yes. The free Starter plan includes dual user profiles — you can manage your own medications and a loved one's schedule from the same app, no extra account needed." },
                { q: "Will I be notified if my parent misses a dose?", a: "Caregiver missed-dose alerts are a Meduler Plus feature. With Plus, you receive a notification whenever a scheduled dose goes unconfirmed." },
                { q: "Can multiple family members share access to one medication schedule?", a: "Meduler Plus supports shared access across multiple caregivers, so siblings or care team members can all stay informed about the same loved one's medication status." },
                { q: "Is Meduler suitable for home health nurses managing multiple patients?", a: "Meduler is designed for family caregivers rather than clinical multi-patient workflows. Nurses often use it to set up demonstrations for their patients and recommend it for home use." },
                { q: "Does Meduler work offline for caregivers with limited connectivity?", a: "Yes. Meduler is offline-first. The complete medication schedule, reminders, and history work without Wi-Fi or mobile data." },
              ].map((faq, i) => (
                <details key={i} className="rounded-xl border border-purple-100 bg-white shadow-sm group">
                  <summary className="p-5 font-semibold text-gray-800 cursor-pointer list-none flex justify-between items-center">
                    {faq.q}
                    <span className="text-purple-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-8 bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Make caregiving easier with Meduler</h2>
            <p className="text-xl mb-8 text-purple-100">
              Start free today and explore Meduler Plus for advanced caregiver features
            </p>
            <a 
              href="/signup" 
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-purple-600 font-medium rounded-lg shadow-lg hover:bg-purple-50 transition-colors"
            >
              Try Meduler Now
            </a>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
};

export default CaregiversPage;
